import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { FiHeart, FiMail, FiPenTool } from "react-icons/fi";
import { FOUNDER_BIRTHDAY, getBirthdayMode, getBirthdayZoneDate } from "../../../config/constants/founderBirthday";
import { useBirthdayMode } from "../../../hooks/useFounderBirthday";
import { fetchBirthdayWishes, submitBirthdayWish } from "../../../services/birthdayService";
import "./BirthdaySection.css";

// ─── Tuning ──────────────────────────────────────────────────────────────────
// RMS level (0–1) that counts as a blow. Room noise sits well under 0.03,
// talking around 0.05–0.12, blowing straight at a laptop/phone mic 0.2+.
const BLOW_THRESHOLD = 0.12;
// How long a breath has to be sustained to put out one more candle.
const MS_PER_CANDLE = 150;
const TAP_SWEEP_MS = 140;
const WISH_MAX = 280;
const WALL_REFRESH_MS = 30 * 1000;

// Each candle's stripe colour, cycling through the Arvix brand trio.
const STRIPES = ["#E31C25", "#39A0ED", "#FFD166"];
// Once the lights come up the stage turns yellow, so the yellow candle
// switches to dark stripes to stay visible.
const DAYLIGHT_STRIPES = ["#E31C25", "#39A0ED", "#2D3142"];
// Signature ink on the card - brand red, blue and dark, each deepened just
// enough to pass contrast as body text on white.
const INKS = ["#C8161E", "#1F6FB2", "#2D3142"];
const CONFETTI_COLORS = ["#E31C25", "#39A0ED", "#FFD166", "#2D3142", "#FDFDFE"];

const prefersReducedMotion = () =>
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// "23 Sept 2026 · 10:42 pm" in birthday (Dhaka) time. Empty when unknown.
const formatWishDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    try {
        return new Intl.DateTimeFormat("en-GB", {
            timeZone: FOUNDER_BIRTHDAY.timeZone,
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        })
            .format(date)
            .replace(",", " ·");
    } catch {
        return "";
    }
};

// ─── Confetti ────────────────────────────────────────────────────────────────
// One burst from the candle line, drawn on a canvas over the stage.
const burstConfetti = (canvas) => {
    if (!canvas || prefersReducedMotion()) return () => {};
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const { width, height } = canvas.getBoundingClientRect();
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    const pieces = Array.from({ length: 180 }, () => ({
        x: width * (0.2 + Math.random() * 0.6),
        y: height * 0.62,
        vx: (Math.random() - 0.5) * 9,
        vy: -(6 + Math.random() * 9),
        size: 5 + Math.random() * 7,
        spin: (Math.random() - 0.5) * 0.3,
        angle: Math.random() * Math.PI,
        color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
        round: Math.random() < 0.3,
    }));

    let frame;
    const start = performance.now();
    const draw = (now) => {
        ctx.clearRect(0, 0, width, height);
        const age = now - start;
        pieces.forEach((p) => {
            p.vy += 0.22;
            p.vx *= 0.99;
            p.x += p.vx;
            p.y += p.vy;
            p.angle += p.spin;
            ctx.save();
            ctx.globalAlpha = Math.max(0, 1 - Math.max(0, age - 3500) / 1500);
            ctx.translate(p.x, p.y);
            ctx.rotate(p.angle);
            ctx.fillStyle = p.color;
            if (p.round) {
                ctx.beginPath();
                ctx.arc(0, 0, p.size / 2.4, 0, Math.PI * 2);
                ctx.fill();
            } else {
                ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
            }
            ctx.restore();
        });
        if (age < 5000) frame = requestAnimationFrame(draw);
        else ctx.clearRect(0, 0, width, height);
    };
    frame = requestAnimationFrame(draw);
    return () => {
        cancelAnimationFrame(frame);
        ctx.clearRect(0, 0, width, height);
    };
};

// ─── Microphone ──────────────────────────────────────────────────────────────
// Opens the mic with the browser's voice processing switched off - noise
// suppression treats a breath as noise and removes it.
const openMicrophone = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
    });
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    const audioCtx = new AudioCtx();
    await audioCtx.resume();
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 1024;
    audioCtx.createMediaStreamSource(stream).connect(analyser);
    const samples = new Float32Array(analyser.fftSize);

    return {
        level() {
            analyser.getFloatTimeDomainData(samples);
            let sum = 0;
            for (let i = 0; i < samples.length; i++) sum += samples[i] * samples[i];
            return Math.sqrt(sum / samples.length);
        },
        close() {
            stream.getTracks().forEach((t) => t.stop());
            audioCtx.close().catch(() => {});
        },
    };
};

const canUseMicrophone = () =>
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    !!(window.AudioContext || window.webkitAudioContext);

// ─── Section ─────────────────────────────────────────────────────────────────
const BirthdaySection = () => {
    const letters = FOUNDER_BIRTHDAY.candleWord.split("");
    const { year } = getBirthdayZoneDate();
    const location = useLocation();
    // Preview-aware: `?birthday=belated` shows the 24th, otherwise the real date.
    const previewMode = useBirthdayMode();
    const mode = previewMode || getBirthdayMode() || "birthday";
    const isBelated = mode === "belated";

    const [lit, setLit] = useState(() => letters.map(() => true));
    const litRef = useRef(lit);
    const [listening, setListening] = useState(false);
    const [status, setStatus] = useState("");
    const allOut = lit.every((l) => !l);

    const stageRef = useRef(null);
    const canvasRef = useRef(null);
    const micRef = useRef(null);
    const rafRef = useRef(null);
    const sweepRef = useRef(null);
    const stopConfettiRef = useRef(() => {});
    const nameInputRef = useRef(null);

    const setWind = (value) => stageRef.current?.style.setProperty("--wind", value.toFixed(3));

    const stopListening = useCallback(() => {
        cancelAnimationFrame(rafRef.current);
        micRef.current?.close();
        micRef.current = null;
        setListening(false);
        setWind(0);
    }, []);

    const extinguish = useCallback((index) => {
        const next = litRef.current.slice();
        next[index] = false;
        litRef.current = next;
        setLit(next);
    }, []);

    // Pick a random lit candle - a real breath doesn't go in order.
    const extinguishRandom = useCallback(() => {
        const remaining = litRef.current.map((l, i) => (l ? i : -1)).filter((i) => i >= 0);
        if (remaining.length) extinguish(remaining[Math.floor(Math.random() * remaining.length)]);
    }, [extinguish]);

    // Celebrate once, the moment the last flame goes out.
    useEffect(() => {
        if (!allOut) return;
        stopListening();
        setStatus(
            isBelated
                ? `All ${letters.length} candles are out. Belated happy birthday, ${FOUNDER_BIRTHDAY.firstName}!`
                : `All ${letters.length} candles are out. Happy birthday, ${FOUNDER_BIRTHDAY.firstName}!`
        );
        stopConfettiRef.current = burstConfetti(canvasRef.current);
    }, [allOut, isBelated, letters.length, stopListening]);

    useEffect(
        () => () => {
            stopListening();
            clearInterval(sweepRef.current);
            stopConfettiRef.current();
        },
        [stopListening]
    );

    const startListening = async () => {
        setStatus("Asking for your microphone…");
        try {
            micRef.current = await openMicrophone();
        } catch {
            setStatus("The microphone is blocked, so tap “Blow with a tap” instead.");
            return;
        }
        setListening(true);
        setStatus("Listening. Blow at your screen.");

        let smoothed = 0;
        let blowing = 0;
        let last = performance.now();
        const tick = (now) => {
            if (!micRef.current) return;
            // Capped so a tab coming back from the background doesn't count
            // the time it was hidden as one long breath.
            const dt = Math.min(now - last, 100);
            last = now;
            smoothed = smoothed * 0.7 + micRef.current.level() * 0.3;
            // The flames lean with the breath, so the visitor sees they're heard.
            setWind(Math.min(smoothed / BLOW_THRESHOLD, 1.6));
            if (smoothed > BLOW_THRESHOLD) {
                blowing += dt;
                while (blowing >= MS_PER_CANDLE) {
                    blowing -= MS_PER_CANDLE;
                    extinguishRandom();
                }
            } else {
                blowing = Math.max(0, blowing - dt);
            }
            rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
    };

    const blowWithTap = () => {
        stopListening();
        clearInterval(sweepRef.current);
        setWind(1);
        let i = 0;
        sweepRef.current = setInterval(() => {
            if (i >= letters.length) {
                clearInterval(sweepRef.current);
                setWind(0);
                return;
            }
            if (litRef.current[i]) extinguish(i);
            i += 1;
        }, TAP_SWEEP_MS);
    };

    const relight = () => {
        stopConfettiRef.current();
        const fresh = letters.map(() => true);
        litRef.current = fresh;
        setLit(fresh);
        setStatus("The candles are lit again.");
    };

    const goToCard = () => {
        const input = nameInputRef.current;
        if (!input) return;
        input.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "center" });
        input.focus({ preventScroll: true });
    };

    const share = async () => {
        const url = `${window.location.origin}/#birthday`;
        const text = isBelated
            ? `It's ${FOUNDER_BIRTHDAY.name}'s belated birthday. Blow out his candles and sign his card. Last day.`
            : `It's ${FOUNDER_BIRTHDAY.name}'s birthday. Blow out his candles and sign his card.`;
        try {
            if (navigator.share) {
                await navigator.share({
                    title: isBelated
                        ? `Belated happy birthday, ${FOUNDER_BIRTHDAY.firstName}`
                        : `Happy birthday, ${FOUNDER_BIRTHDAY.firstName}`,
                    text,
                    url,
                });
                return;
            }
            await navigator.clipboard.writeText(url);
            setStatus("Link copied. Send it to a friend.");
        } catch {
            // The share sheet was dismissed; nothing to report.
        }
    };

    // A shared link lands on /#birthday. The layout scrolls to the top on
    // route change after this mounts, so wait for it before jumping here.
    useEffect(() => {
        if (location.hash !== "#birthday") return undefined;
        const t = setTimeout(() => {
            document.getElementById("birthday")?.scrollIntoView({ block: "start" });
        }, 150);
        return () => clearTimeout(t);
    }, [location.hash]);

    const litCount = lit.filter(Boolean).length;

    return (
        <section id="birthday" className="bday" aria-labelledby="bday-title">
            <div
                ref={stageRef}
                className="bday-stage"
                data-lights={allOut ? "on" : "off"}
                style={{ "--glow": litCount / letters.length }}
            >
                <canvas ref={canvasRef} className="bday-confetti" aria-hidden="true" />

                <div className="bday-head">
                    <p className="bday-date">
                        {isBelated ? (
                            <>
                                His birthday · 23 September, {year} · today 24 September{" "}
                                <span className="bday-badge" aria-label="Belated birthday, last day">
                                    Belated · last day
                                </span>
                            </>
                        ) : (
                            `His birthday · 23 September, ${year}`
                        )}
                    </p>
                    <h2 id="bday-title" className="bday-title">
                        {isBelated
                            ? allOut
                                ? `Belated happy birthday, ${FOUNDER_BIRTHDAY.firstName}`
                                : `A belated wish for ${FOUNDER_BIRTHDAY.firstName}`
                            : allOut
                              ? `Happy birthday, ${FOUNDER_BIRTHDAY.firstName}`
                              : `Make a wish for ${FOUNDER_BIRTHDAY.firstName}`}
                    </h2>
                    <p className="bday-sub">
                        {isBelated
                            ? allOut
                                ? "Yesterday was his birthday and the candles are out again. Everyone at the studio, and everyone stopping by late, wishes him a bright year ahead. The card closes tonight."
                                : "Yesterday was our founder’s birthday. Missed it? Blow the candles out late and leave a belated line. The card closes tonight."
                            : allOut
                              ? "Founder of Arvix Creative and the designer behind most of what you see here. Everyone at the studio, and everyone who stops by today, wishes you a bright year ahead."
                              : "It’s our founder’s birthday. Blow at your microphone and watch the flames lean, or blow them out with a tap."}
                    </p>
                </div>

                <div
                    className="bday-cake"
                    role="img"
                    aria-label={`Birthday candles shaped like the letters ${letters.join(" ")}, ${litCount} of ${letters.length} still lit`}
                >
                    {letters.map((letter, i) => (
                        <span
                            key={i}
                            className="bday-candle"
                            data-lit={lit[i] ? "true" : "false"}
                            style={{
                                "--stripe": STRIPES[i % STRIPES.length],
                                "--stripe-day": DAYLIGHT_STRIPES[i % DAYLIGHT_STRIPES.length],
                                "--i": i,
                            }}
                        >
                            <span className="bday-flame" />
                            <span className="bday-smoke" />
                            <span className="bday-wick" />
                            <span className="bday-glyph">{letter}</span>
                        </span>
                    ))}
                    <span className="bday-plate" />
                </div>

                <div className="bday-controls">
                    {!allOut && (
                        <>
                            {canUseMicrophone() &&
                                (listening ? (
                                    <button type="button" className="bday-btn bday-btn--ghost" onClick={stopListening}>
                                        Stop listening
                                    </button>
                                ) : (
                                    <button type="button" className="bday-btn bday-btn--primary" onClick={startListening}>
                                        Blow into your mic
                                    </button>
                                ))}
                            <button type="button" className="bday-btn bday-btn--ghost" onClick={blowWithTap}>
                                Blow with a tap
                            </button>
                        </>
                    )}
                    {allOut && (
                        <>
                            <button type="button" className="bday-btn bday-btn--primary" onClick={goToCard}>
                                Sign his card
                            </button>
                            <button type="button" className="bday-btn bday-btn--ghost" onClick={share}>
                                Share the celebration
                            </button>
                            <button type="button" className="bday-btn bday-btn--link" onClick={relight}>
                                Light the candles again
                            </button>
                        </>
                    )}
                </div>
                <p className="bday-status" role="status" aria-live="polite">
                    {status}
                </p>
            </div>

            <BirthdayCard year={year} nameInputRef={nameInputRef} isBelated={isBelated} />
        </section>
    );
};

// ─── The card everyone signs ────────────────────────────────────────────────
// Open on the birthday (23 Sept) and the belated day (24 Sept), every year.
const BirthdayCard = ({ year, nameInputRef, isBelated }) => {
    const [wishes, setWishes] = useState([]);
    const [total, setTotal] = useState(0);
    const [isOpen, setIsOpen] = useState(true);
    const [loadState, setLoadState] = useState("loading");
    const [form, setForm] = useState({ name: "", message: "", website: "" });
    const [sending, setSending] = useState(false);
    const [formNote, setFormNote] = useState({ tone: "", text: "" });
    const [freshId, setFreshId] = useState(null);

    const load = useCallback(async () => {
        try {
            const res = await fetchBirthdayWishes(year);
            setWishes(res.data);
            setTotal(res.total);
            setIsOpen(res.isOpen);
            setLoadState("ready");
        } catch {
            setLoadState((s) => (s === "ready" ? s : "error"));
        }
    }, [year]);

    // Keep the card live while the tab is visible, so new signatures appear.
    useEffect(() => {
        load();
        const timer = setInterval(() => {
            if (document.visibilityState === "visible") load();
        }, WALL_REFRESH_MS);
        return () => clearInterval(timer);
    }, [load]);

    const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (form.name.trim().length < 2 || form.message.trim().length < 2) {
            setFormNote({ tone: "error", text: "Add your name and a wish, both at least 2 characters." });
            return;
        }
        setSending(true);
        setFormNote({ tone: "", text: "" });
        try {
            const wish = await submitBirthdayWish(form);
            if (wish) {
                setWishes((list) => [wish, ...list.filter((w) => w.id !== wish.id)]);
                setTotal((t) => t + 1);
                setFreshId(wish.id);
            }
            setForm({ name: form.name, message: "", website: "" });
            setFormNote({ tone: "ok", text: "Your wish is on the card." });
        } catch (err) {
            setFormNote({ tone: "error", text: err.message });
        } finally {
            setSending(false);
        }
    };

    const remaining = WISH_MAX - form.message.length;

    const countLabel =
        loadState !== "ready"
            ? "Opening the card…"
            : total === 0
              ? "No signatures yet. Yours could be first."
              : `${total} ${total === 1 ? "person has" : "people have"} signed so far.`;

    return (
        <div className="bday-card">
            <p className="bday-card-kicker">
                <FiMail aria-hidden="true" className="bday-icon" /> His birthday · 23 September {year} · card open
                23–24 only
            </p>
            <div className="bday-card-inner">
                <span className="bday-tape" aria-hidden="true" />
                <div className="bday-card-left">
                    <p className="bday-card-eyebrow">
                        {isBelated
                            ? `Belated post · birthday 23 September · today 24 September ${year}`
                            : `Birthday post · 23 September ${year}`}
                    </p>
                    <h3 className="bday-card-title">
                        {isBelated ? "Send a belated wish" : "Sign his birthday card"}
                    </h3>
                    <p className="bday-card-blurb">
                        {isBelated
                            ? "His birthday was 23 September and you missed it. Add a belated line, he’ll still read every word."
                            : "Write it the way you’d write inside a real card: short, warm, in your own words."}
                    </p>
                    <p className="bday-card-count">
                        <span className="bday-seal" aria-hidden="true">
                            {loadState === "ready" ? total : "…"}
                        </span>
                        {countLabel}
                    </p>

                    {isOpen ? (
                        <form className="bday-form" onSubmit={submit} noValidate>
                            <label className="bday-label" htmlFor="bday-name">
                                Your name
                            </label>
                            <input
                                ref={nameInputRef}
                                id="bday-name"
                                className="bday-input"
                                value={form.name}
                                onChange={update("name")}
                                maxLength={60}
                                autoComplete="name"
                                placeholder="e.g. Habib Bhai"
                                required
                            />

                            <label className="bday-label" htmlFor="bday-message">
                                Your wish <span className="bday-label-hint">(a line or two he&rsquo;ll keep)</span>
                            </label>
                            <textarea
                                id="bday-message"
                                className="bday-input bday-textarea"
                                value={form.message}
                                onChange={update("message")}
                                maxLength={WISH_MAX}
                                rows={5}
                                required
                                placeholder={
                                    isBelated
                                        ? `Belated happy birthday, ${FOUNDER_BIRTHDAY.firstName}! Sorry I missed the 23rd…`
                                        : `Happy birthday, ${FOUNDER_BIRTHDAY.firstName}! Wishing you…`
                                }
                                aria-describedby="bday-message-count"
                            />
                            <p id="bday-message-count" className="bday-counter" data-low={remaining < 30 ? "true" : "false"}>
                                {remaining} characters left
                            </p>

                            {/* Honeypot - hidden from people, filled in by bots. */}
                            <div className="bday-hp" aria-hidden="true">
                                <label htmlFor="bday-website">Website</label>
                                <input
                                    id="bday-website"
                                    tabIndex={-1}
                                    autoComplete="off"
                                    value={form.website}
                                    onChange={update("website")}
                                />
                            </div>

                            <button type="submit" className="bday-btn bday-btn--ink" disabled={sending}>
                                <FiPenTool aria-hidden="true" className="bday-icon" />{" "}
                                {sending ? "Adding your wish…" : isBelated ? "Add my belated wish" : "Sign the card"}
                            </button>
                            <p className="bday-form-note" data-tone={formNote.tone} role="status" aria-live="polite">
                                {formNote.text || " "}
                            </p>
                        </form>
                    ) : (
                        <p className="bday-closed">
                            <span className="bday-closed-seal" aria-hidden="true">Sealed</span>
                            The card takes signatures on 23 September. Come back then to leave your ink on it.
                        </p>
                    )}
                </div>

                <span className="bday-spine" aria-hidden="true" />

                <div className="bday-card-right">
                    <div className="bday-wall-head">
                        <h4 className="bday-wall-title">Inside the card</h4>
                        <span className="bday-wall-count" aria-label={`${total} wishes`}>
                            <FiHeart aria-hidden="true" className="bday-icon" />{" "}
                            {loadState === "ready" ? total : "…"}
                        </span>
                    </div>
                    {loadState === "loading" && (
                        <ul className="bday-wishes" aria-hidden="true">
                            {[0, 1, 2, 3].map((i) => (
                                <li key={i} className="bday-wish bday-wish--skeleton">
                                    <span className="bday-skel-line" />
                                    <span className="bday-skel-line bday-skel-line--short" />
                                </li>
                            ))}
                        </ul>
                    )}
                    {loadState === "error" && (
                        <div className="bday-blank">
                            <p className="bday-blank-title">The card didn’t open.</p>
                            <p className="bday-blank-sub">The paper got stuck. Refresh the page to try again.</p>
                            <button type="button" className="bday-btn bday-btn--ghost bday-btn--dark" onClick={load}>
                                Try again
                            </button>
                        </div>
                    )}
                    {loadState === "ready" && wishes.length === 0 && (
                        <div className="bday-blank">
                            <p className="bday-blank-title">This page is still blank.</p>
                            <p className="bday-blank-sub">Be the first to pick up the pen. Your words will sit right here.</p>
                        </div>
                    )}
                    {wishes.length > 0 && (
                        <ul className="bday-wishes">
                            {wishes.map((w) => {
                                const when = formatWishDate(w.created_at);
                                return (
                                    <li
                                        key={w.id}
                                        className="bday-wish"
                                        data-fresh={w.id === freshId ? "true" : "false"}
                                        style={{
                                            "--ink": INKS[Math.abs(w.id) % INKS.length],
                                            "--tilt": `${((Math.abs(w.id) * 37) % 5) - 2}deg`,
                                        }}
                                    >
                                        <p className="bday-wish-text">“{w.message}”</p>
                                        <p className="bday-wish-name">~ {w.name}</p>
                                        {when && (
                                            <p className="bday-wish-date">
                                                Signed {when}
                                            </p>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>
            <p className="bday-card-foot">New wishes appear live · Kind words only, signed with love</p>
        </div>
    );
};

export default BirthdaySection;
